import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  VStack,
  Grid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useThemeColors } from "@/hooks/useThemeColors";
import { useAuthStore } from "@/store/auth";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import {
  createBillingStatementSchema,
  updateBillingStatementSchema,
  type CreateBillingStatementFormData,
  type UpdateBillingStatementFormData,
} from "@/lib/validations/auth";

/** If date is ISO string, parse and return yyyy-mm-dd in local time; otherwise return as-is. */
function normalizeApiDate(dateRaw: unknown): string {
  if (dateRaw == null) return "";
  if (typeof dateRaw !== "string") return "";
  const s = dateRaw.trim();
  if (!s) return "";
  if (s.includes("T")) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.substring(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return s.length >= 10 ? s.substring(0, 10) : s;
}

export type BillingStatementFormData = CreateBillingStatementFormData;

export interface BillingStatementFormProps {
  /** When provided, form is in edit mode and fetches by ID. */
  billingStatementId?: number;
  /** Required for create mode. */
  soaId?: number;
  /** Registered / shipping line name (create: from parent; edit: from fetched). */
  registeredName?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function BillingStatementForm({
  billingStatementId,
  soaId,
  registeredName: propRegisteredName = "",
  onSuccess,
  onCancel,
}: BillingStatementFormProps) {
  const colors = useThemeColors();
  const { showError, showSuccess } = useToastNotification();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [displayRegisteredName, setDisplayRegisteredName] = useState(propRegisteredName);
  const isMountedRef = useRef(true);

  const isEditMode = !!billingStatementId;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<BillingStatementFormData>({
    resolver: zodResolver(
      isEditMode ? updateBillingStatementSchema : createBillingStatementSchema
    ),
    defaultValues: {
      billing_statement_no: "",
      payment_term: "",
      ci_date: "",
      due_date: "",
      bus_style: "",
      has_details: false,
    },
  });

  const watchHasDetails = watch("has_details");

  // Fetch billing statement when in edit mode and autofill from API
  useEffect(() => {
    if (!isEditMode || !billingStatementId) return;
    isMountedRef.current = true;
    const fetchBillingStatement = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/billing-statements/${billingStatementId}`);
        if (!isMountedRef.current) return;
        const data = response.data?.data ?? response.data ?? {};

        const billingStatementNo =
          data.billing_statement_no ?? (data as Record<string, unknown>).billing_statement_no ?? "";

        const soa = data.statement_of_account ?? (data as Record<string, unknown>).statement_of_account;
        const shippingLine = data.shipping_line ?? soa?.shipping_line ?? (soa as Record<string, unknown>)?.shipping_line;
        const registeredName =
          shippingLine?.name ?? (shippingLine as Record<string, unknown>)?.name ?? "";

        const ciDateRaw = data.ci_date ?? (data as Record<string, unknown>).ci_date;
        const dueDateRaw = data.due_date ?? (data as Record<string, unknown>).due_date;
        const paymentTerm = data.payment_term ?? (data as Record<string, unknown>).payment_term ?? "";
        const busStyle = data.bus_style ?? (data as Record<string, unknown>).bus_style ?? "";
        const hasDetails = data.has_details ?? (data as Record<string, unknown>).has_details ?? false;

        setDisplayRegisteredName(String(registeredName));
        reset({
          billing_statement_no: String(billingStatementNo),
          payment_term: paymentTerm,
          ci_date: normalizeApiDate(ciDateRaw),
          due_date: normalizeApiDate(dueDateRaw),
          bus_style: busStyle,
          has_details: hasDetails === true,
        });
      } catch (error: unknown) {
        if (isMountedRef.current) showError(error);
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    };

    fetchBillingStatement();
    return () => {
      isMountedRef.current = false;
    };
  }, [isEditMode, billingStatementId, reset, showError]);

  const onSubmit = async (data: BillingStatementFormData) => {
    const payload = {
      prepared_by: currentUserId ?? undefined,
      payment_term: data.payment_term?.trim() || null,
      ci_date: data.ci_date?.trim() || null,
      due_date: data.due_date?.trim() || null,
      bus_style: data.bus_style?.trim() || null,
      has_details: data.has_details ?? false,
    };

    setIsSubmitting(true);
    try {
      if (isEditMode && billingStatementId) {
        await apiClient.put(`/billing-statements/${billingStatementId}`, {
          billing_statement_no: data.billing_statement_no?.trim() ?? "",
          ...payload,
        });
        showSuccess("Success", "Billing statement updated successfully");
      } else if (soaId != null) {
        await apiClient.post("/billing-statements/generate", {
          statement_of_account_id: soaId,
          billing_statement_no: data.billing_statement_no.trim(),
          ...payload,
        });
        showSuccess("Success", "Billing statement created successfully");
      } else {
        showError("SOA ID is required to create a billing statement");
        return;
      }
      onSuccess();
    } catch (error: unknown) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color={colors.text}>
          Loading billing statement...
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={4} align="stretch">
              {/* Registered Name (read-only) */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                  Billed To: <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <FormControl>
                  <Input
                    value={displayRegisteredName}
                    readOnly
                    bg={colors.sectionBg}
                    borderColor={colors.border}
                    color={colors.text}
                  />
                </FormControl>
              </Grid>

              {/* Billing Statement No: required; editable in create and edit */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                  Billing Statement No:{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <FormControl isInvalid={!!errors.billing_statement_no}>
                  <Input
                    {...register("billing_statement_no")}
                    placeholder="e.g. BS-2024-001"
                    borderColor={colors.border}
                    color={colors.text}
                  />
                  <FormErrorMessage>{errors.billing_statement_no?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              {/* Payment Term (optional) */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                  Payment Term:
                </FormLabel>
                <FormControl isInvalid={!!errors.payment_term}>
                  <Input
                    {...register("payment_term", { maxLength: 255 })}
                    placeholder="e.g. Net 30"
                    borderColor={colors.border}
                    color={colors.text}
                  />
                  <FormErrorMessage>{errors.payment_term?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              {/* CI Date and Due Date side by side */}
              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                gap={4}
                alignItems="start"
              >
                <FormControl isInvalid={!!errors.ci_date}>
                  <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                    CI Date:
                  </FormLabel>
                  <Input
                    type="date"
                    {...register("ci_date")}
                    borderColor={colors.border}
                    color={colors.text}
                  />
                  <FormErrorMessage>{errors.ci_date?.message}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.due_date}>
                  <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                    Due Date:
                  </FormLabel>
                  <Input
                    type="date"
                    {...register("due_date")}
                    borderColor={colors.border}
                    color={colors.text}
                  />
                  <FormErrorMessage>{errors.due_date?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              {/* Business style, Has details */}
              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                gap={4}
                alignItems="flex-end"
              >
                <FormControl isInvalid={!!errors.bus_style}>
                  <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                    Business Style:
                  </FormLabel>
                  <Input
                    {...register("bus_style", { maxLength: 255 })}
                    placeholder="Business style"
                    borderColor={colors.border}
                    color={colors.text}
                  />
                  <FormErrorMessage>{errors.bus_style?.message}</FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel pt={2} fontWeight="medium" color={colors.text} mb={0}>
                    Has Details
                  </FormLabel>
                  <Checkbox
                    {...register("has_details")}
                    colorScheme="blue"
                    isChecked={!!watchHasDetails}
                    onChange={(e) => setValue("has_details", e.target.checked)}
                  >
                    <Text color={colors.text}>Include details</Text>
                  </Checkbox>
                </FormControl>
              </Grid>

              {/* Action Buttons */}
              <HStack spacing={4} justify="flex-end" pt={2}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  borderColor={colors.border}
                  color={colors.text}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  loadingText={isEditMode ? "Updating..." : "Creating..."}
                >
                  {isEditMode ? "Update" : "Create"}
                </Button>
              </HStack>
        </VStack>
      </Box>
    </VStack>
  );
}
